import type { AiFeedbackItem } from '../lib/api/aiFeedback'

const displayValue = (value: string | number | null | undefined) =>
  value == null || value === '' ? 'Not captured' : String(value)

const titleCase = (value: string) =>
  value.length === 0 ? 'Unknown' : `${value[0].toUpperCase()}${value.slice(1)}`

const fencedBlock = (content: string, language = 'text') => {
  const longestBacktickRun = Math.max(2, ...Array.from(content.matchAll(/`+/g), (match) => match[0].length))
  const fence = '`'.repeat(longestBacktickRun + 1)
  return `${fence}${language}\n${content}\n${fence}`
}

const appendContent = (lines: string[], heading: string, content?: string | null) => {
  lines.push(`### ${heading}`, '')
  lines.push(content == null || content === '' ? '_No content captured._' : fencedBlock(content), '')
}

const feedbackTargetUserMessage = (item: AiFeedbackItem) => {
  const savedMessages = item.savedConversation?.messages ?? []
  const targetAssistantIndex = savedMessages.findIndex(
    (message) => message.interactionId === item.id
  )
  for (let index = targetAssistantIndex - 1; index >= 0; index -= 1) {
    if (savedMessages[index].role === 'user') {
      return savedMessages[index].content
    }
  }

  const rawUserMessages = item.messages.filter((message) => message.role === 'user')
  return rawUserMessages[rawUserMessages.length - 1]?.content
}

const displayedResponse = (item: AiFeedbackItem) => {
  if (item.displayedResponse?.content) {
    return item.displayedResponse.content
  }

  return item.channel === 'mobile-coach' ? null : item.response?.content
}

export const buildAiFeedbackMarkdown = (item: AiFeedbackItem, exportedAt: Date) => {
  const lines = [
    '# HikeStrong AI Feedback Conversation',
    '',
    '> Complete debug export from Basecamp `/ai-feedback`.',
    '',
    '## Export details',
    '',
    `- Exported at: ${exportedAt.toISOString()}`,
    `- Interaction ID: ${displayValue(item.id)}`,
    `- Saved conversation ID: ${displayValue(item.savedConversation?.id)}`,
    `- Interaction created at: ${displayValue(item.createdAt)}`,
    '',
    '## Conversation context',
    '',
    `- Mobile user ID: ${displayValue(item.userId)}`,
    `- Channel: ${displayValue(item.channel)}`,
    `- Agent role: ${displayValue(item.agentRole)}`,
    `- System prompt ID: ${displayValue(item.systemPromptId)}`,
    `- Model: ${displayValue(item.model)}`,
    `- Subscription: ${displayValue(item.subscriptionTier)} / ${displayValue(item.subscriptionStatus)}`,
    `- Duration: ${item.durationMs == null ? 'Not captured' : `${item.durationMs} ms`}`,
    '',
    '## Feedback',
    '',
    `- Rating: ${displayValue(item.feedback)}`,
    `- Submitted at: ${displayValue(item.feedbackAt)}`,
    `- Reviewed at: ${displayValue(item.feedbackReviewedAt)}`,
    `- Reviewed by user ID: ${displayValue(item.feedbackReviewedByUserId)}`,
    ''
  ]

  appendContent(lines, 'User comment', item.feedbackComment)

  lines.push('## Feedback target', '')
  appendContent(lines, 'User message', feedbackTargetUserMessage(item))
  appendContent(lines, 'Response shown to user', displayedResponse(item))

  lines.push('## Saved user-facing conversation', '')
  if (!item.savedConversation) {
    lines.push('_No saved conversation was found for this interaction._', '')
  } else {
    lines.push(
      `- Title: ${displayValue(item.savedConversation.title)}`,
      `- Created at: ${displayValue(item.savedConversation.createdAt)}`,
      `- Updated at: ${displayValue(item.savedConversation.updatedAt)}`,
      ''
    )
    item.savedConversation.messages.forEach((message, index) => {
      appendContent(lines, `Message ${index + 1} — ${titleCase(message.role)}`, message.content)
    })
  }

  lines.push(
    '## Raw model interaction',
    '',
    '> Debug data captured before server-side validation or user-facing formatting.',
    ''
  )
  if (item.messages.length === 0) {
    lines.push('_No raw request messages captured._', '')
  } else {
    item.messages.forEach((message, index) => {
      appendContent(lines, `Raw message ${index + 1} — ${titleCase(message.role)}`, message.content)
    })
  }

  appendContent(
    lines,
    `Raw model response — ${titleCase(item.response?.role ?? 'assistant')}`,
    item.response?.content
  )

  lines.push(
    '## Usage',
    '',
    `- Prompt tokens: ${displayValue(item.usage?.promptTokens)}`,
    `- Completion tokens: ${displayValue(item.usage?.completionTokens)}`,
    `- Total tokens: ${displayValue(item.usage?.totalTokens)}`,
    '',
    '## Raw export data',
    '',
    fencedBlock(JSON.stringify({ exportedAt: exportedAt.toISOString(), interaction: item }, null, 2), 'json'),
    ''
  )

  return lines.join('\n')
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'conversation'

export const createAiFeedbackExportFilename = (item: AiFeedbackItem, exportedAt: Date) => {
  const stamp = exportedAt.toISOString().replace(/[:.]/g, '-')
  return `hikestrong-ai-feedback-${slugify(item.id)}-${stamp}.md`
}

export const downloadAiFeedbackMarkdown = (item: AiFeedbackItem) => {
  const exportedAt = new Date()
  const markdown = buildAiFeedbackMarkdown(item, exportedAt)
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = createAiFeedbackExportFilename(item, exportedAt)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
